import { useMemo } from "react";
import type { MatchRow } from "@shared/api";
import { aggregateMatches, rangeRows, type MatchAggregate } from "@shared/lib/matchAggregate";
import { ChartState } from "@features/charts/ChartState";

const WEEK_SECONDS = 7 * 86_400;

interface RecapData {
  currentRange: { start: number; end: number };
  previousRange: { start: number; end: number };
  current: MatchAggregate;
  previous: MatchAggregate;
  currentRows: MatchRow[];
  bestMatch: MatchRow | null;
}

type TrendTone = "good" | "bad" | "neutral";

interface TrendCell {
  label: string;
  value: string;
  delta: string | null;
  tone: TrendTone;
}

export function WeeklyRecap({ rows }: { rows: MatchRow[] | null }) {
  const data = useMemo(() => (rows ? buildRecap(rows) : null), [rows]);
  const cells = useMemo(() => (data ? buildCells(data) : []), [data]);

  if (!data) {
    return (
      <section className="weekly-recap">
        <div className="section-heading">
          <h2>Weekly recap</h2>
        </div>
        <ChartState>Loading recap…</ChartState>
      </section>
    );
  }

  if (data.current.observedMatches === 0 && data.previous.observedMatches === 0) {
    return (
      <section className="weekly-recap">
        <div className="section-heading">
          <h2>Weekly recap</h2>
        </div>
        <ChartState>No tracked matches in the last 14 days.</ChartState>
      </section>
    );
  }

  const narrative = buildNarrative(data);
  return (
    <section className="weekly-recap">
      <div className="section-heading">
        <h2>Weekly recap</h2>
        <span className="records-sub">
          {formatDateRange(data.currentRange.start, data.currentRange.end)}
        </span>
      </div>
      <article className="recap-panel">
        <p className="recap-narrative">{narrative}</p>
        <div className="recap-grid">
          {cells.map((cell) => (
            <div className="recap-cell" key={cell.label}>
              <span className="recap-cell-label">{cell.label}</span>
              <strong className="recap-cell-value">{cell.value}</strong>
              {cell.delta && <span className={`recap-cell-delta tone-${cell.tone}`}>{cell.delta}</span>}
            </div>
          ))}
        </div>
        {data.bestMatch && <BestMatchSpotlight match={data.bestMatch} />}
      </article>
    </section>
  );
}

function BestMatchSpotlight({ match }: { match: MatchRow }) {
  const kd = match.deaths_delta === 0 ? match.kills_delta : match.kills_delta / match.deaths_delta;
  const date = new Date(match.window_end * 1000);
  const result = match.wins_delta >= 1 ? "W" : match.losses_delta >= 1 ? "L" : "·";
  return (
    <div className="recap-spotlight">
      <span>Match of the week</span>
      <strong>
        {match.kills_delta}/{match.deaths_delta} · {kd.toFixed(2)} K/D
      </strong>
      <p>
        {date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · {result} ·{" "}
        {match.gamemode_hint ?? "unknown"}
        {match.map_hint ? ` · ${match.map_hint}` : ""}
        {match.damage_delta > 0 ? ` · ${match.damage_delta.toLocaleString()} dmg` : ""}
      </p>
    </div>
  );
}

function buildRecap(rows: MatchRow[]): RecapData {
  const now = Math.floor(Date.now() / 1000);
  const currentStart = now - WEEK_SECONDS;
  const previousStart = now - 2 * WEEK_SECONDS;

  const currentRows = rangeRows(rows, currentStart, now + 1);
  const previousRows = rangeRows(rows, previousStart, currentStart);

  const bestMatch = pickBestMatch(currentRows);

  return {
    currentRange: { start: currentStart, end: now },
    previousRange: { start: previousStart, end: currentStart },
    current: aggregateMatches(currentRows),
    previous: aggregateMatches(previousRows),
    currentRows,
    bestMatch,
  };
}

function pickBestMatch(rows: MatchRow[]): MatchRow | null {
  const singles = rows.filter((row) => row.merged_match_count === 1 && row.kills_delta > 0);
  if (singles.length === 0) return null;
  const sorted = [...singles].sort((a, b) => {
    const aKd = a.deaths_delta === 0 ? a.kills_delta : a.kills_delta / a.deaths_delta;
    const bKd = b.deaths_delta === 0 ? b.kills_delta : b.kills_delta / b.deaths_delta;
    return bKd - aKd || b.kills_delta - a.kills_delta || b.damage_delta - a.damage_delta;
  });
  return sorted[0] ?? null;
}

function buildCells(data: RecapData): TrendCell[] {
  const { current, previous } = data;

  return [
    numericCell("Matches", current.observedMatches, previous.observedMatches, (v) => v.toLocaleString(), "higher"),
    numericCell("K/D", current.kd, previous.kd, (v) => v.toFixed(2), "higher"),
    numericCell("Kills", current.kills, previous.kills, (v) => v.toLocaleString(), "higher"),
    numericCell(
      "Win rate",
      current.winRate,
      previous.winRate,
      (v) => `${(v * 100).toFixed(0)}%`,
      "higher",
      (diff) => `${(diff * 100).toFixed(0)}pt`,
    ),
    numericCell("Hours", current.activeSeconds / 3600, previous.activeSeconds / 3600, (v) => v.toFixed(1), "higher"),
    numericCell(
      "Kills/match",
      current.avgKillsPerMatch,
      previous.avgKillsPerMatch,
      (v) => v.toFixed(1),
      "higher",
    ),
  ];
}

function numericCell(
  label: string,
  currentValue: number | null,
  previousValue: number | null,
  format: (value: number) => string,
  direction: "higher" | "lower",
  diffFormatter?: (diff: number) => string,
): TrendCell {
  if (currentValue === null) return { label, value: "—", delta: null, tone: "neutral" };
  const displayValue = format(currentValue);
  if (previousValue === null || (previousValue === 0 && currentValue === 0)) {
    return { label, value: displayValue, delta: null, tone: "neutral" };
  }
  const diff = currentValue - previousValue;
  if (!Number.isFinite(diff) || diff === 0) return { label, value: displayValue, delta: "flat", tone: "neutral" };
  const better = direction === "higher" ? diff > 0 : diff < 0;
  const sign = diff > 0 ? "+" : "−";
  const formatted = diffFormatter ? diffFormatter(Math.abs(diff)) : format(Math.abs(diff));
  return { label, value: displayValue, delta: `${sign}${formatted}`, tone: better ? "good" : "bad" };
}

function buildNarrative(data: RecapData): string {
  const { current, previous } = data;
  if (current.observedMatches === 0) {
    return `No tracked matches in the last 7 days. Previous week: ${previous.observedMatches.toLocaleString()} matches at ${previous.kd === null ? "—" : previous.kd.toFixed(2)} K/D.`;
  }
  const hoursCurrent = current.activeSeconds / 3600;
  const hoursPrevious = previous.activeSeconds / 3600;
  const deltaKd = (current.kd ?? 0) - (previous.kd ?? 0);
  const kdTrend = !Number.isFinite(deltaKd) || deltaKd === 0 ? "flat" : deltaKd > 0 ? "up" : "down";
  const hoursTrend = hoursCurrent > hoursPrevious ? "more" : hoursCurrent < hoursPrevious ? "less" : "about the same";
  const winHint = current.winRate === null ? "" : ` with a ${(current.winRate * 100).toFixed(0)}% win rate`;
  return `${current.observedMatches.toLocaleString()} matches over ${hoursCurrent.toFixed(1)}h${winHint}; K/D ${current.kd?.toFixed(2) ?? "—"} (${kdTrend} vs last week), ${hoursTrend} playtime.`;
}

function formatDateRange(start: number, end: number): string {
  const s = new Date(start * 1000);
  const e = new Date(end * 1000);
  const fmt = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
  return `${fmt.format(s)} → ${fmt.format(e)}`;
}
